import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useDebounce } from './useDebounce'
import {
  listAdminPlans,
  deleteAdminPlan,
  transferPlanOwnership,
  getAdminPlanShares,
  createAdminPlanShare,
  deleteAdminPlanShare,
} from '@/api/adminPlans'
import type { OwnershipTransferResult } from '@/api/adminPlans'
import type { PaginatedPlansResponse } from '@/types'
import type { PlanShareRead, PlanSharesResponse } from '@/api/shares'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'

interface ApiErrorPayload {
  detail?: string
  title?: string
}

function getApiErrorMessage(
  error: AxiosError<ApiErrorPayload>,
  fallback: string
) {
  return error.response?.data?.detail ?? error.response?.data?.title ?? fallback
}

// --- List plans ---

interface UseAdminPlansListParams {
  search: string
  status: string
  planType: string
  ownerId: string
  page: number
  pageSize: number
}

export function useAdminPlansList({
  search,
  status,
  planType,
  ownerId,
  page,
  pageSize,
}: UseAdminPlansListParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    status,
    planType,
    ownerId,
    page,
    pageSize,
  }

  return useQuery<PaginatedPlansResponse>({
    queryKey: queryKeys.adminPlans.list(filters),
    queryFn: () =>
      listAdminPlans({
        search: debouncedSearch || undefined,
        status: status === 'all' ? undefined : status,
        plan_type: planType === 'all' ? undefined : planType,
        owner_id: ownerId || undefined,
        offset: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

// --- Plan shares ---

export function useAdminPlanShares(planId: string | undefined) {
  return useQuery<PlanSharesResponse>({
    queryKey: queryKeys.adminPlans.shares(planId ?? ''),
    queryFn: () => getAdminPlanShares(planId!),
    enabled: !!planId,
  })
}

export function useCreateAdminPlanShare(planId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<
    PlanShareRead,
    AxiosError<ApiErrorPayload>,
    { userId: string }
  >({
    mutationFn: ({ userId }) => createAdminPlanShare(planId!, userId),
    onSuccess: () => {
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.adminPlans.shares(planId),
        })
      }
      toast.success('Plan shared successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to share plan'))
    },
  })
}

export function useDeleteAdminPlanShare(planId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<ApiErrorPayload>, { shareId: string }>({
    mutationFn: ({ shareId }) => deleteAdminPlanShare(planId!, shareId),
    onSuccess: () => {
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.adminPlans.shares(planId),
        })
      }
      toast.success('Share removed successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to remove share'))
    },
  })
}

// --- Transfer ownership ---

export function useTransferAdminPlanOwnership() {
  const queryClient = useQueryClient()

  return useMutation<
    OwnershipTransferResult,
    AxiosError<ApiErrorPayload>,
    { planId: string; newOwnerId: string; keepAsEditor?: boolean }
  >({
    mutationFn: ({ planId, newOwnerId, keepAsEditor }) =>
      transferPlanOwnership(planId, newOwnerId, keepAsEditor),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminPlans.all })
      const newOwnerName =
        result.to_owner.display_name ?? result.to_owner.email ?? 'new owner'
      toast.success(`Ownership transferred to ${newOwnerName}`)
    },
    onError: error => {
      toast.error(
        getApiErrorMessage(error, 'Failed to transfer plan ownership')
      )
    },
  })
}

// --- Delete plan ---

export function useDeleteAdminPlan() {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    AxiosError<ApiErrorPayload>,
    { planId: string; force?: boolean }
  >({
    mutationFn: ({ planId, force }) => deleteAdminPlan(planId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminPlans.all })
      toast.success('Plan deleted successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to delete plan'))
    },
  })
}
